import { useCallback, useEffect, useMemo, useState } from "react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import {
    Check, ChevronDown, ChevronRight, ClipboardList, Filter, Link2, Minus,
    Pencil, RefreshCw, Save, ShieldCheck, Users, X,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { CRE_HANDOVER_URL } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

/**
 * The Sales-to-CRE handover: what was sold, what was promised, and what is
 * left to do before go-live.
 *
 * The form is built from the field descriptors the API returns, so which
 * fields appear is the tenant's setting (Settings → CRE Handover) and not a
 * hardcoded list here. Fields switched off still arrive for leads that already
 * answered them, flagged `is_retired`, and render read-only.
 *
 * Laid out for someone copying from the handover spreadsheet with it open
 * beside them: sections in the sheet's own order, everything expanded, one
 * Edit for the whole form, and a save bar that follows down the page. The
 * fiddly controls are the ones that would otherwise cost the most clicks —
 * 12 integrations are a tick-grid rather than a dropdown, and Yes/No is two
 * buttons rather than a select to open, read and pick from.
 *
 * Fields flagged `is_required` block Closed Won until they are answered. They
 * are marked here so the rep fills them while the deal is still in front of
 * them, rather than discovering the rule at the moment they try to win it. The
 * server holds the same list and refuses the transition on it — this is the
 * signpost, not the lock.
 *
 * Fields flagged `is_auto` are read from the lead or the accepted quotation
 * and are never editable here. They show their value with the source beside
 * it — "from quotation QTN-2026-0042" — because a greyed-out box with no
 * explanation reads as broken, and because a CRE who disagrees with the value
 * needs to know which record to go and fix.
 */

const EMPTY_FOR = (type) => (type === "multi" ? [] : "");

const isBlank = (v) =>
    v === "" || v === null || v === undefined || (Array.isArray(v) && !v.length);

const CREHandoverPanel = ({ customerId, customerName, initial, onSaved }) => {
    const { fetch, create } = useApiMethods();

    const [payload, setPayload] = useState(initial || null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving]   = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft]     = useState({});
    const [spocDraft, setSpocDraft] = useState({});
    const [collapsed, setCollapsed] = useState({});
    const [onlyEmpty, setOnlyEmpty] = useState(false);

    const seed = useCallback((res) => {
        setPayload(res);
        const values = {};
        (res.data || []).forEach(f => {
            values[f.key] = f.value ?? EMPTY_FOR(f.type);
        });
        setDraft(values);
        const spocs = {};
        (res.spocs || []).forEach(s => {
            spocs[s.id] = { spoc_type: s.spoc_type || "", authority: s.authority || "" };
        });
        setSpocDraft(spocs);
    }, []);

    const load = useCallback(() => {
        if (!customerId) return;
        setLoading(true);
        fetch(`${CRE_HANDOVER_URL}?customer_id=${customerId}`)
            .then(res => {
                if (res?.status === "success") seed(res);
                else ToastMsg("error", res?.message || "Failed to load the handover");
            })
            .catch(() => ToastMsg("error", "Failed to load the handover"))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, seed]);

    // The lead page has usually fetched this already to decide whether to show
    // the tab at all. Reuse that response rather than asking again.
    useEffect(() => {
        if (initial) seed(initial);
        else load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    // Memoised because the derived lists below depend on it; a fresh []
    // every render would rebuild them every render.
    const groups     = useMemo(() => payload?.groups || [], [payload]);
    const readOnly   = !!payload?.is_read_only;
    const allFields  = useMemo(() => groups.flatMap(g => g.fields || []), [groups]);
    // "n of m recorded" counts only what somebody has to type. An auto field is
    // not an outstanding task, and including it would make the form look
    // part-done before anyone had touched it.
    const liveFields = useMemo(
        () => allFields.filter(f => !f.is_retired && !f.is_auto), [allFields]);
    const filled     = liveFields.filter(f => f.has_value).length;
    const pct        = liveFields.length
        ? Math.round((filled / liveFields.length) * 100) : 0;

    const setField = (key, value) => setDraft(d => ({ ...d, [key]: value }));

    const setSpoc = (id, key, value) =>
        setSpocDraft(s => ({ ...s, [id]: { ...s[id], [key]: value } }));

    // A field whose `depends_on` answer has not been given yet is not asked.
    // Reads the draft, not the saved value, so ticking LIS reveals the machine
    // count immediately rather than after a save.
    const applies = (f) => {
        if (!f.depends_on) return true;
        const on = draft[f.depends_on.field];
        return Array.isArray(on) && on.includes(f.depends_on.value);
    };

    const save = () => {
        const values = {};
        allFields.forEach(f => {
            // Retired and auto fields are shown but not collected. Submitting
            // either is rejected server-side, and validation is all-or-nothing
            // — it would fail the whole save over a field nobody touched.
            if (f.is_retired || f.is_auto) return;
            const v = draft[f.key];
            if (!isBlank(v) || f.has_value) values[f.key] = v;
        });

        const spocs = (payload?.spocs || [])
            .filter(s => {
                const d = spocDraft[s.id] || {};
                return (d.spoc_type || "") !== (s.spoc_type || "") ||
                       (d.authority || "") !== (s.authority || "");
            })
            .map(s => ({ id: s.id, ...spocDraft[s.id] }));

        if (!Object.keys(values).length && !spocs.length) { setEditing(false); return; }

        setSaving(true);
        create(CRE_HANDOVER_URL, {
            customer_id: customerId,
            values,
            ...(spocs.length ? { spocs } : {}),
        })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", "Handover saved");
                    setEditing(false);
                    // The lead page decides whether Closed Won is allowed from
                    // its own copy of this payload. Hand it the fresh one so it
                    // is not checking a list from before this save.
                    onSaved?.(res);
                    // Merge rather than replace: the save response carries the
                    // fields and spocs but not the tenant flags the tab needs.
                    seed({ ...payload, ...res });
                } else {
                    ToastMsg("error", res?.message || "Could not save the handover");
                }
            })
            .catch(() => ToastMsg("error", "Could not save the handover"))
            .finally(() => setSaving(false));
    };

    // ── read-only rendering of one answer ──
    const displayValue = (f) => {
        const v = f.value;
        const blank = <span className="text-gray-300 dark:text-gray-600">—</span>;
        if (isBlank(v)) return blank;

        if (f.type === "boolean") {
            return (
                <span className={cn(
                    "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
                    v ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                      : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                )}>
                    {v ? <Check className="h-2.5 w-2.5" /> : <Minus className="h-2.5 w-2.5" />}
                    {v ? "Yes" : "No"}
                </span>
            );
        }
        if (f.type === "date") return dayjs(v).format("DD MMM YYYY");
        if (f.type === "money") return "₹" + Number(v).toLocaleString("en-IN");
        if (f.type === "multi") {
            return (
                <span className="flex flex-wrap gap-1">
                    {v.map(s => (
                        <span key={s}
                            className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 text-[10px]">
                            {s}
                        </span>
                    ))}
                </span>
            );
        }
        if (f.type === "textarea") {
            return <span className="whitespace-pre-wrap">{String(v)}</span>;
        }
        return (
            <>{String(v)}{f.unit && <span className="text-gray-400 ml-0.5">{f.unit}</span>}</>
        );
    };

    // ── editable control for one field ──
    const inputCls = "w-full text-xs px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-indigo-400";

    const editor = (f) => {
        const v = draft[f.key];

        if (f.type === "boolean") {
            // Two buttons, not a dropdown: the answer is visible without
            // opening anything, and recording one is a single click.
            return (
                <div className="flex items-center gap-1">
                    {[["Yes", true], ["No", false]].map(([label, val]) => (
                        <button key={label} type="button"
                            onClick={() => setField(f.key, v === val ? "" : val)}
                            className={cn(
                                "h-7 px-3 rounded-lg text-[11px] font-medium border transition",
                                v === val
                                    ? "border-indigo-500 bg-indigo-600 text-white"
                                    : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            )}>
                            {label}
                        </button>
                    ))}
                    {v !== "" && v !== null && v !== undefined && (
                        <button type="button" onClick={() => setField(f.key, "")}
                            title="Back to not recorded"
                            className="h-7 w-7 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center">
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </div>
            );
        }

        if (f.type === "multi") {
            // A tick-grid rather than a multi-select. Nineteen modules in a
            // dropdown is nineteen open-scroll-click cycles; here every option
            // is on screen and each is one click.
            const chosen = Array.isArray(v) ? v : [];
            const toggle = (opt) => setField(f.key,
                chosen.includes(opt) ? chosen.filter(o => o !== opt) : [...chosen, opt]);
            return (
                <div className="flex flex-wrap gap-1.5">
                    {(f.options || []).map(opt => {
                        const on = chosen.includes(opt);
                        return (
                            <button key={opt} type="button" onClick={() => toggle(opt)}
                                className={cn(
                                    "inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-medium border transition",
                                    on
                                        ? "border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                                        : "border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                                )}>
                                {on
                                    ? <Check className="h-3 w-3" />
                                    : <span className="h-3 w-3 rounded-full border border-current opacity-40" />}
                                {opt}
                            </button>
                        );
                    })}
                    {chosen.length > 0 && (
                        <button type="button" onClick={() => setField(f.key, [])}
                            className="h-7 px-2 text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            Clear
                        </button>
                    )}
                </div>
            );
        }

        if (f.type === "choice") {
            return (
                <Select value={v || undefined} onChange={val => setField(f.key, val ?? "")}
                    className="w-full" size="small" allowClear
                    placeholder={`Select ${f.label}`}
                    options={(f.options || []).map(o => ({ value: o, label: o }))} />
            );
        }
        if (f.type === "date") {
            return (
                <DatePicker value={v ? dayjs(v) : null}
                    onChange={d => setField(f.key, d ? d.format("YYYY-MM-DD") : "")}
                    className="w-full" size="small" format="DD MMM YYYY"
                    placeholder="Pick a date" />
            );
        }
        if (f.type === "textarea") {
            return <textarea rows={3} value={v || ""}
                onChange={e => setField(f.key, e.target.value)}
                className={cn(inputCls, "resize-y")} placeholder={f.placeholder} />;
        }
        if (f.type === "money") {
            return (
                <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">₹</span>
                    <input type="number" min="0" step="0.01" value={v ?? ""}
                        onChange={e => setField(f.key, e.target.value)}
                        className={cn(inputCls, "pl-6")} placeholder="0.00" />
                </div>
            );
        }
        if (f.type === "number") {
            return (
                <div className="flex items-center gap-1.5">
                    <input type="number" min="0" step="1" value={v ?? ""}
                        onChange={e => setField(f.key, e.target.value)}
                        className={inputCls} placeholder="0" />
                    {f.unit && (
                        <span className="text-[10px] text-gray-400 whitespace-nowrap">{f.unit}</span>
                    )}
                </div>
            );
        }
        return <input type="text" value={v || ""}
            onChange={e => setField(f.key, e.target.value)}
            className={inputCls} placeholder={f.placeholder} />;
    };

    if (!customerId) return null;

    if (loading && !payload) {
        return (
            <div className="flex items-center gap-2 p-4">
                <RefreshCw className="h-3.5 w-3.5 animate-spin text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Loading the handover…</span>
            </div>
        );
    }

    if (payload && !payload.is_enabled && !payload.has_handover) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-4">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                    <ClipboardList className="h-3.5 w-3.5" /> CRE Handover
                </p>
                <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                    Not switched on for this account. A super admin can turn it on under
                    Settings → CRE Handover.
                </p>
            </div>
        );
    }

    if (!allFields.length) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-4">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                    <ClipboardList className="h-3.5 w-3.5" /> CRE Handover
                </p>
                <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                    Every field is switched off. A super admin can turn them back on under
                    Settings → CRE Handover.
                </p>
            </div>
        );
    }

    const spocs = payload?.spocs || [];
    // The first section still missing something — the answer to "where do I
    // carry on", which is the question anyone reopening a half-filled sheet has.
    const nextGroup = groups.find(g => (g.fields || [])
        .some(f => !f.is_retired && !f.has_value && applies(f)));
    const missingCount = allFields
        .filter(f => !f.is_retired && !f.is_auto && !f.has_value && applies(f)).length;
    // What is actually holding up Closed Won, as opposed to what is merely
    // blank. The server sends the authoritative list; falling back to the
    // descriptors keeps this right if the panel is fed an older payload.
    const blockers = payload?.required_missing
        ?? allFields.filter(f => f.is_required && !f.is_retired && !f.has_value && applies(f))
                    .map(f => ({ key: f.key, label: f.label }));

    return (
        <div className="space-y-3 pb-2">
            {/* ── header: what this is, how far along, and the one Edit ── */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-800 dark:text-gray-100">
                            <ClipboardList className="h-4 w-4 text-indigo-500" />
                            CRE Handover
                        </p>
                        <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                            What sales is handing the implementation team
                            {customerName ? <> for <span className="font-medium">{customerName}</span></> : null}.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        {!readOnly && (
                            <button onClick={() => setOnlyEmpty(o => !o)}
                                title="Show only what is still missing"
                                className={cn(
                                    "h-7 px-2.5 rounded-md text-[11px] font-medium border flex items-center gap-1",
                                    onlyEmpty
                                        ? "border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                                        : "border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
                                )}>
                                <Filter className="h-3 w-3" /> Missing only
                            </button>
                        )}
                        {readOnly ? null : editing ? (
                            <>
                                <button disabled={saving} onClick={save}
                                    className="h-7 px-3 rounded-md text-[11px] font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white flex items-center gap-1">
                                    <Save className="h-3 w-3" /> {saving ? "Saving…" : "Save"}
                                </button>
                                <button onClick={() => { setEditing(false); setOnlyEmpty(false); load(); }}
                                    className="h-7 px-2.5 rounded-md text-[11px] border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700">
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button onClick={() => setEditing(true)}
                                className="h-7 px-3 rounded-md text-[11px] font-medium bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1">
                                <Pencil className="h-3 w-3" /> Fill in
                            </button>
                        )}
                    </div>
                </div>

                {/* progress: the number, the bar, and where to carry on */}
                <div className="mt-2.5">
                    <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400 mb-1">
                        <span>{filled} of {liveFields.length} recorded</span>
                        <span className="tabular-nums font-medium">{pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div className={cn("h-full rounded-full transition-all",
                            pct === 100 ? "bg-emerald-500" : "bg-indigo-500")}
                            style={{ width: `${pct}%` }} />
                    </div>
                    {!editing && nextGroup && (
                        <p className="mt-1.5 text-[10px] text-gray-400">
                            Next up: <span className="font-medium text-gray-500 dark:text-gray-300">
                                {nextGroup.group}
                            </span>
                        </p>
                    )}
                </div>

                {readOnly && (
                    <p className="mt-2.5 text-[10px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-2.5 py-1.5">
                        Handovers have been switched off for this account. What was already
                        recorded is kept and readable, but it cannot be changed.
                    </p>
                )}

                {/* Why the rest of this matters right now: the deal cannot be
                    marked won until these are answered. Named individually —
                    "the handover is incomplete" sends somebody scrolling. */}
                {!readOnly && (blockers.length ? (
                    <div className="mt-2.5 text-[10px] text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-2.5 py-1.5">
                        <span className="font-semibold">
                            This deal cannot be marked Closed Won yet.
                        </span>{" "}
                        {blockers.length} required field{blockers.length === 1 ? "" : "s"} still
                        blank: {blockers.map(b => b.label).join(", ")}.
                    </div>
                ) : (
                    <p className="mt-2.5 text-[10px] text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5">
                        <ShieldCheck className="h-3 w-3 flex-shrink-0" />
                        Everything required for Closed Won is recorded.
                    </p>
                ))}
            </div>

            {/* ── SPOCs: who the CRE will actually be talking to ── */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700/40 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                        <Users className="h-3 w-3" /> SPOCs
                        <span className="normal-case tracking-normal text-gray-400">
                            · who decides, and who is only told
                        </span>
                    </p>
                </div>
                {spocs.length ? (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {spocs.map(s => {
                            const d = spocDraft[s.id] || {};
                            return (
                                <div key={s.id} className="px-3 py-2 flex items-center gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs font-medium text-gray-800 dark:text-gray-100 truncate">
                                            {s.name || "Unnamed contact"}
                                            {s.is_primary_contact && (
                                                <span className="ml-1.5 text-[9px] px-1.5 py-px rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300">
                                                    primary
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-[10px] text-gray-400 truncate">
                                            {[s.designation, s.contact_number, s.email]
                                                .filter(Boolean).join(" · ") || "No details recorded"}
                                        </p>
                                    </div>

                                    {editing && !readOnly ? (
                                        <div className="flex items-center gap-1.5">
                                            <Select value={d.spoc_type || undefined}
                                                onChange={v => setSpoc(s.id, "spoc_type", v ?? "")}
                                                size="small" allowClear placeholder="SPOC type"
                                                style={{ minWidth: 120 }}
                                                options={(payload?.spoc_types || [])
                                                    .map(o => ({ value: o, label: o }))} />
                                            <Select value={d.authority || undefined}
                                                onChange={v => setSpoc(s.id, "authority", v ?? "")}
                                                size="small" allowClear placeholder="Authority"
                                                style={{ minWidth: 140 }}
                                                options={(payload?.spoc_authorities || [])
                                                    .map(o => ({ value: o, label: o }))} />
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1.5">
                                            {s.spoc_type ? (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                    {s.spoc_type}
                                                </span>
                                            ) : null}
                                            {s.authority ? (
                                                <span className={cn(
                                                    "text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1",
                                                    s.authority === "Decision maker"
                                                        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                                        : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                                                )}>
                                                    {s.authority === "Decision maker" && (
                                                        <ShieldCheck className="h-2.5 w-2.5" />
                                                    )}
                                                    {s.authority}
                                                </span>
                                            ) : null}
                                            {!s.spoc_type && !s.authority && (
                                                <span className="text-[10px] text-gray-300 dark:text-gray-600">
                                                    not classified
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="px-3 py-3 text-[11px] text-gray-400 dark:text-gray-500">
                        No contacts on this lead yet. Add them from the lead's Contacts
                        section — the SPOC type and authority can then be set here.
                    </p>
                )}
            </div>

            {/* ── the sheet itself, section by section ── */}
            {groups.map(group => {
                const shown = (group.fields || []).filter(f =>
                    // An auto field is never "missing" in the sense the filter
                    // means — there is nothing for this person to go and do
                    // about it on this screen.
                    applies(f) && (!onlyEmpty || (!f.has_value && !f.is_auto)));
                if (!shown.length) return null;   // nothing to ask in this section

                const isOpen = !collapsed[group.group];
                const done = group.filled_count >= group.total_count && group.total_count > 0;

                return (
                    <div key={group.group}
                        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                        <button type="button"
                            onClick={() => setCollapsed(c => ({ ...c, [group.group]: isOpen }))}
                            className="w-full px-3 py-2 flex items-center gap-2 bg-gray-50 dark:bg-gray-700/40 border-b border-gray-100 dark:border-gray-700 text-left hover:bg-gray-100 dark:hover:bg-gray-700/60 transition">
                            {isOpen
                                ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                : <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                            <span className="min-w-0 flex-1">
                                <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">
                                    {group.group}
                                </span>
                                {group.help_text && (
                                    <span className="block text-[10px] text-gray-400 mt-0.5">
                                        {group.help_text}
                                    </span>
                                )}
                            </span>
                            <span className={cn(
                                "text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 tabular-nums",
                                done
                                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                    : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                            )}>
                                {group.filled_count}/{group.total_count}
                            </span>
                        </button>

                        {isOpen && (
                            <div className="p-3 grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">
                                {shown.map(f => {
                                    // A tick-grid or a paragraph needs the full
                                    // width; a name or a number does not.
                                    const wide = f.type === "multi" || f.type === "textarea";
                                    const canEdit = editing && !f.is_retired &&
                                        !f.is_auto && !readOnly;
                                    return (
                                        <div key={f.key} className={cn(
                                            wide && "lg:col-span-2",
                                            canEdit || wide
                                                ? "space-y-1"
                                                : "flex items-start justify-between gap-3"
                                        )}>
                                            <label className="text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0"
                                                title={f.is_retired
                                                    ? "No longer collected — kept because this lead already has an answer"
                                                    : (f.help_text || undefined)}>
                                                {f.label}
                                                {f.is_required && !f.is_retired && !f.has_value && (
                                                    <span className="ml-0.5 text-red-500" title="Required before this deal can be marked Closed Won">*</span>
                                                )}
                                                {f.is_retired && (
                                                    <span className="ml-1 text-[9px] px-1 py-px rounded bg-gray-100 dark:bg-gray-700 text-gray-400">
                                                        archived
                                                    </span>
                                                )}
                                                {f.is_auto && f.source && (
                                                    <span className="ml-1 text-[9px] px-1 py-px rounded bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 inline-flex items-center gap-0.5">
                                                        <Link2 className="h-2 w-2" />
                                                        {f.source}
                                                    </span>
                                                )}
                                                {canEdit && f.help_text && (
                                                    <span className="block text-[10px] text-gray-400 italic font-normal mt-0.5">
                                                        {f.help_text}
                                                    </span>
                                                )}
                                            </label>
                                            {canEdit ? editor(f) : (
                                                <span className={cn(
                                                    "text-[11px] font-medium",
                                                    wide ? "block" : "text-right",
                                                    f.is_retired
                                                        ? "text-gray-400 dark:text-gray-500"
                                                        : "text-gray-800 dark:text-gray-200"
                                                )}>
                                                    {displayValue(f)}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}

            {onlyEmpty && !missingCount && (
                <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2.5">
                    <p className="text-[11px] text-emerald-800 dark:text-emerald-200">
                        Nothing left to fill in — every field this account collects has an
                        answer. Turn off <span className="font-medium">Missing only</span> to
                        read the whole handover.
                    </p>
                </div>
            )}

            {/* Save follows the page down: the form is long enough that the
                header button is off screen by the time it is wanted. */}
            {editing && !readOnly && (
                <div className="sticky bottom-0 -mx-1 px-1 pb-1 pt-2 bg-gradient-to-t from-white dark:from-gray-800 to-transparent">
                    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-gray-800 shadow-sm px-3 py-2 flex items-center justify-between gap-3">
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            {filled} of {liveFields.length} recorded before this edit
                        </span>
                        <div className="flex items-center gap-2">
                            <button onClick={() => { setEditing(false); setOnlyEmpty(false); load(); }}
                                className="h-7 px-2.5 rounded-md text-[11px] border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700">
                                Cancel
                            </button>
                            <button disabled={saving} onClick={save}
                                className="h-7 px-3 rounded-md text-[11px] font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white flex items-center gap-1">
                                <Save className="h-3 w-3" /> {saving ? "Saving…" : "Save handover"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CREHandoverPanel;
